import * as React from 'react';
import { Search, X } from 'lucide-react';

interface SearchBarProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
}

export function SearchBar({
  value,
  onChange,
  onClear,
  placeholder = 'Search...',
  ...props
}: SearchBarProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClear = () => {
    onChange('');
    onClear?.();
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Search
        style={{
          position: 'absolute',
          insetInlineStart: '14px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '18px',
          height: '18px',
          color: isFocused ? 'var(--accent-primary)' : 'var(--text-muted)',
          pointerEvents: 'none',
          transition: `color var(--motion-duration-fast) var(--motion-ease-standard)`,
        }}
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px',
          paddingInlineStart: '44px',
          paddingInlineEnd: '44px',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--bg-primary)',
          border: `2px solid ${isFocused ? 'var(--accent-primary)' : isHovered ? '#CBD5E1' : 'var(--border-primary)'}`,
          color: 'var(--text-primary)',
          fontSize: '15px',
          fontFamily: "'Inter', system-ui, sans-serif",
          outline: 'none',
          boxShadow: isFocused ? '0 0 0 4px rgba(6, 182, 212, 0.1)' : 'none',
          transition: `border-color var(--motion-duration-fast) var(--motion-ease-standard), box-shadow var(--motion-duration-fast) var(--motion-ease-standard)`,
        }}
        {...props}
      />
      {value && (
        <button
          onClick={handleClear}
          style={{
            position: 'absolute',
            insetInlineEnd: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'var(--bg-tertiary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-tertiary)',
          }}
          aria-label="Clear search"
        >
          <X style={{ width: '14px', height: '14px' }} />
        </button>
      )}
    </div>
  );
}
